import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@copro/api";
import { enqueueSnackbar } from "../../../components/GlobalSnackbar/snackbarStore";
import { keys } from "./keys";

type Variables = {
  sourceId: string;
  targetId: string;
};

async function mergeSubTopics(sourceId: string, targetId: string) {
  if (sourceId === targetId) {
    throw new Error('Source and target sub-topics must be different.');
  }

  // Fetch notes from both to merge them later.
  const { data: pair, error: fetchError } = await apiClient
    .from('sub_topics')
    .select('id, notes')
    .in('id', [sourceId, targetId]);

  if (fetchError) throw fetchError;

  const source = pair?.find(s => s.id === sourceId);
  const target = pair?.find(s => s.id === targetId);

  if (!source || !target) {
    throw new Error('Sub-topic not found.');
  }

  // Reassign children FKs from source to target.
  const reassign = [
    apiClient.from('subject_actions').update({ subject_id: targetId }).eq('subject_id', sourceId),
    apiClient.from('subject_tasks').update({ subject_id: targetId }).eq('subject_id', sourceId),
    apiClient.from('reminders').update({ subject_id: targetId }).eq('subject_id', sourceId),
    apiClient.from('events').update({ subject_id: targetId }).eq('subject_id', sourceId),
    apiClient.from('documents').update({ sub_topic_id: targetId }).eq('sub_topic_id', sourceId),
  ];

  const results = await Promise.all(reassign);
  for (const res of results) {
    if (res.error) throw res.error;
  }

  // Merge notes: append source notes to target if any.
  const sourceNotes = source.notes?.trim();
  if (sourceNotes) {
    const targetNotes = target.notes?.trim();
    const mergedNotes = targetNotes
      ? `${targetNotes}\n\n---\n\n${sourceNotes}`
      : sourceNotes;

    const { error: notesError } = await apiClient
      .from('sub_topics')
      .update({ notes: mergedNotes })
      .eq('id', targetId);
    if (notesError) throw notesError;
  }

  // Delete the source sub-topic.
  const { error: deleteError } = await apiClient
    .from('sub_topics')
    .delete()
    .eq('id', sourceId);
  if (deleteError) throw deleteError;
}

export function useMergeSubTopics() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sourceId, targetId }: Variables) => mergeSubTopics(sourceId, targetId),
    onSuccess: () => {
      enqueueSnackbar('Sous-sujets fusionnés.', 'success');
      queryClient.invalidateQueries({ queryKey: keys.all });
    }
  });
}
